/**
 * Boten Formulier - Nieuw/Bewerken
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { botenService, BootCreate } from '../../services/boten.service';
import { bootModellenService, BootModel } from '../../services/bootModellen.service';
import { relationsService, Relation } from '../../services/relations.service';
import apiFetch from '../../services/api';
import { bootDocumentenService, BootDocument, DOCUMENT_TYPES } from '../../services/bootDocumenten.service';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import './Boten.css';

// Interfaces voor boot onderdelen
interface OnderdeelCatalogus {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
  levensduur_maanden: number | null;
}

interface BootOnderdeel {
  id: number;
  boot_id: number;
  voorraad_item_id: number | null;
  onderdeel_type: string;
  fabrikant: string | null;
  artikelnummer: string | null;
  // Het gemonteerde exemplaar. Een serienummer bestaat pas als bekend is in welke
  // boot het ding zit, dus staat het hier en niet bij het product.
  positie: string | null;
  serienummer: string | null;
  bouwjaar: number | null;
  installatie_datum: string | null;
  draaiuren_huidig: number | null;
  levensduur_maanden: number | null;
  vervangingsdatum: string | null;
}

interface BotenFormProps {
  bootId?: number;
}

const BotenForm = ({ bootId }: BotenFormProps) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isEditMode = !!bootId;

  /* Documenten bij deze boot: CE-verklaring, schema's, handleidingen.
     Toegevoegd 23-09-2026; hiervoor kon je die nergens kwijt. */
  const [documenten, setDocumenten] = useState<BootDocument[]>([]);
  const [docType, setDocType] = useState('');
  const [docTitel, setDocTitel] = useState('');
  const [docBestand, setDocBestand] = useState<File | null>(null);
  const [docBezig, setDocBezig] = useState(false);
  const [docZichtbaar, setDocZichtbaar] = useState(false);
  const [docTypeEigen, setDocTypeEigen] = useState(false);
  
  // Bepaal of gebruiker mag bewerken
  const canEdit = isEditMode ? hasPermission('boten_bewerken') : hasPermission('boten_aanmaken');
  const isReadOnly = isEditMode && !canEdit;

  const [formData, setFormData] = useState<BootCreate>({
    boot_model_id: 0,
    eigenaar_id: 0,
    bootnaam: '',
    registratienummer: '',
    bouwjaar: undefined,
    lengte: undefined,
    breedte: undefined,
    diepgang: undefined,
    gewicht: undefined,
    materiaal: '',
    ligplaats: '',
    mmsi: '',
    opmerkingen: '',
    actief: true,
  });

  const [bootModellen, setBootModellen] = useState<BootModel[]>([]);
  const [relaties, setRelaties] = useState<Relation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedBootModel, setSelectedBootModel] = useState<BootModel | null>(null);

  // Zoekbare dropdown states
  const [bootModelSearch, setBootModelSearch] = useState('');
  const [eigenaarSearch, setEigenaarSearch] = useState('');
  const [bootModelDropdownOpen, setBootModelDropdownOpen] = useState(false);
  const [eigenaarDropdownOpen, setEigenaarDropdownOpen] = useState(false);

  // Boot onderdelen states
  const [bootOnderdelen, setBootOnderdelen] = useState<BootOnderdeel[]>([]);
  const [onderdelenCatalogus, setOnderdelenCatalogus] = useState<OnderdeelCatalogus[]>([]);
  const [onderdeelSearch, setOnderdeelSearch] = useState('');
  const [onderdeelDropdownOpen, setOnderdeelDropdownOpen] = useState(false);
  const [selectedOnderdeel, setSelectedOnderdeel] = useState<OnderdeelCatalogus | null>(null);
  const [nieuwOnderdeelDatum, setNieuwOnderdeelDatum] = useState(new Date().toISOString().split('T')[0]);
  const [nieuwOnderdeelPositie, setNieuwOnderdeelPositie] = useState('');
  const [nieuwOnderdeelSerienummer, setNieuwOnderdeelSerienummer] = useState('');
  const [nieuwOnderdeelBouwjaar, setNieuwOnderdeelBouwjaar] = useState<number | ''>('');
  const [nieuwOnderdeelDraaiuren, setNieuwOnderdeelDraaiuren] = useState<number | ''>('');

  // Bewerken modal states
  const [editingOnderdeel, setEditingOnderdeel] = useState<BootOnderdeel | null>(null);
  const [editDatum, setEditDatum] = useState('');
  const [editLevensduur, setEditLevensduur] = useState<number | ''>('');
  const [editPositie, setEditPositie] = useState('');
  const [editSerienummer, setEditSerienummer] = useState('');
  const [editBouwjaar, setEditBouwjaar] = useState<number | ''>('');
  const [editDraaiuren, setEditDraaiuren] = useState<number | ''>('');

  const loadOptions = useCallback(async () => {
    try {
      // Laad alle boot modellen (mogelijk meer dan 100)
      let allModellen: BootModel[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const modellen = await bootModellenService.getAll({ actief: true, limit: 100, skip: offset });
        allModellen = [...allModellen, ...modellen];
        hasMore = modellen.length === 100;
        offset += 100;
      }

      // Laad alle relaties (mogelijk meer dan 100)
      let allRelaties: Relation[] = [];
      let relatiesOffset = 0;
      let relatiesHasMore = true;

      while (relatiesHasMore) {
        const relatiesData = await relationsService.getAll({ actief: true, limit: 100, offset: relatiesOffset });
        allRelaties = [...allRelaties, ...relatiesData.items];
        relatiesHasMore = relatiesData.items.length === 100;
        relatiesOffset += 100;
      }

      /* De onderdelencatalogus is de voorraad. Stond op '/api/v1/onderdelen-catalogus',
         een endpoint dat niet bestaat: dat gaf 404, de catch ving het stil af en de lijst
         bleef leeg met "Geen onderdelen gevonden". Gemeld door Dylan 23-09-2026. */
      try {
        const onderdelenData = await apiFetch('/api/v1/voorraad/items/all');
        setOnderdelenCatalogus(onderdelenData);
      } catch (catalogusErr) {
        console.warn('[loadOptions] Voorraaditems niet op te halen:', catalogusErr);
        setOnderdelenCatalogus([]);
      }

      setBootModellen(allModellen);
      setRelaties(allRelaties);
    } catch (err: any) {
      setError(err.message || 'Kon opties niet laden');
    }
  }, []);

  const loadBoot = useCallback(async () => {
    if (!bootId) {
      return;
    }

    try {
      setLoading(true);
      const boot = await botenService.getById(bootId);

      // Haal boot model op om specificaties te kunnen invullen als die ontbreken
      const bootModel = await bootModellenService.getById(boot.boot_model_id);

      // Vul boot model search veld in met de naam van het boot model
      const displayText = bootModel.fabrikant === 'Onbekend'
        ? `${bootModel.model} (Onbekend merk)`
        : `${bootModel.fabrikant} ${bootModel.model}${bootModel.type_boot ? ` (${bootModel.type_boot})` : ''}`;
      setBootModelSearch(displayText);
      setSelectedBootModel(bootModel);

      // Haal eigenaar op om naam in te vullen
      if (boot.eigenaar_id) {
        try {
          const eigenaar = await relationsService.getById(boot.eigenaar_id);
          // Voor particuliere klanten: toon naam ipv "Particulier"
          if (eigenaar.bedrijfsnaam === 'Particulier' && eigenaar.achternaam) {
            const naamParts = [eigenaar.voornaam, eigenaar.tussenvoegsel, eigenaar.achternaam].filter(Boolean);
            setEigenaarSearch(naamParts.join(' '));
          } else {
            setEigenaarSearch(eigenaar.bedrijfsnaam);
          }
        } catch (err) {
          console.error('Kon eigenaar niet laden:', err);
        }
      }

      setFormData({
        boot_model_id: boot.boot_model_id,
        eigenaar_id: boot.eigenaar_id,
        bootnaam: boot.bootnaam || '',
        registratienummer: boot.registratienummer || '',
        bouwjaar: boot.bouwjaar,
        // Gebruik boot specificaties, of val terug op boot model specificaties als boot specificaties leeg zijn
        lengte: boot.lengte ?? bootModel.lengte,
        breedte: boot.breedte ?? bootModel.breedte,
        diepgang: boot.diepgang ?? bootModel.diepgang,
        gewicht: boot.gewicht ?? bootModel.gewicht,
        materiaal: boot.materiaal || '',
        ligplaats: boot.ligplaats || '',
        mmsi: boot.mmsi || '',
        opmerkingen: boot.opmerkingen || '',
        actief: boot.actief,
      });

      // Laad bestaande onderdelen
      const bestaandeOnderdelen = await apiFetch(`/api/v1/boot-onderdelen/boot/${bootId}?include_vervangen=false`);
      setBootOnderdelen(bestaandeOnderdelen);

      // Laad de documenten bij deze boot
      setDocumenten(await bootDocumentenService.getByBoot(bootId));
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van boot');
    } finally {
      setLoading(false);
    }
  }, [bootId]);

  useEffect(() => {
    loadOptions();
    if (isEditMode) {
      loadBoot();
    }
  }, [isEditMode, loadOptions, loadBoot]);

  // Update selectedBootModel wanneer formData.boot_model_id of bootModellen wijzigt
  useEffect(() => {
    if (formData.boot_model_id && bootModellen.length > 0) {
      const model = bootModellen.find(m => m.id === formData.boot_model_id);
      setSelectedBootModel(model || null);
      if (model) {
        // Voor generieke modellen, toon alleen het model naam
        const displayText = model.fabrikant === 'Onbekend'
          ? `${model.model} (Onbekend merk)`
          : `${model.fabrikant} ${model.model}${model.type_boot ? ` (${model.type_boot})` : ''}`;
        setBootModelSearch(displayText);
      }
    }
  }, [formData.boot_model_id, bootModellen]);

  // Update eigenaar search wanneer eigenaar_id wijzigt
  useEffect(() => {
    if (formData.eigenaar_id && relaties.length > 0) {
      const eigenaar = relaties.find(r => r.id === formData.eigenaar_id);
      if (eigenaar) {
        setEigenaarSearch(getEigenaarDisplayNaam(eigenaar));
      }
    }
  }, [formData.eigenaar_id, relaties]);

  // Click outside handler om dropdowns te sluiten
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-select')) {
        setBootModelDropdownOpen(false);
        setEigenaarDropdownOpen(false);
        setOnderdeelDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Gefilterde boot modellen met generieke modellen bovenaan
  const filteredBootModellen = bootModellen.filter(model => {
    const searchTerm = bootModelSearch.toLowerCase();
    const modelText = `${model.fabrikant} ${model.model} ${model.type_boot || ''}`.toLowerCase();
    return modelText.includes(searchTerm);
  }).sort((a, b) => {
    // Zet generieke modellen (Onbekend fabrikant) altijd bovenaan
    const aIsGeneric = a.fabrikant === 'Onbekend';
    const bIsGeneric = b.fabrikant === 'Onbekend';

    if (aIsGeneric && !bIsGeneric) return -1;
    if (!aIsGeneric && bIsGeneric) return 1;

    // Als beide generiek zijn, sorteer op model (Motorboot voor Zeilboot)
    if (aIsGeneric && bIsGeneric) {
      return a.model.localeCompare(b.model);
    }

    // Voor niet-generieke modellen, sorteer alfabetisch op fabrikant + model
    const aText = `${a.fabrikant} ${a.model}`;
    const bText = `${b.fabrikant} ${b.model}`;
    return aText.localeCompare(bText);
  });

  // Helper: Bepaal display naam voor eigenaar
  const getEigenaarDisplayNaam = (relatie: Relation) => {
    if (relatie.bedrijfsnaam === 'Particulier' && relatie.achternaam) {
      const naamParts = [relatie.voornaam, relatie.tussenvoegsel, relatie.achternaam].filter(Boolean);
      return naamParts.join(' ');
    }
    return relatie.bedrijfsnaam;
  };

  // Gefilterde relaties
  const filteredRelaties = relaties.filter(relatie => {
    const searchTerm = eigenaarSearch.toLowerCase();
    const displayNaam = getEigenaarDisplayNaam(relatie);
    return displayNaam.toLowerCase().includes(searchTerm);
  });

  // Gefilterde onderdelen catalogus
  const filteredOnderdelen = onderdelenCatalogus.filter(onderdeel => {
    const searchTerm = onderdeelSearch.toLowerCase();
    const onderdeelText = `${onderdeel.onderdeel_type} ${onderdeel.fabrikant || ''} ${onderdeel.model || ''} ${onderdeel.leverancier || ''}`.toLowerCase();
    return onderdeelText.includes(searchTerm);
  }).sort((a, b) => {
    // Sorteer op type, dan op fabrikant
    if (a.onderdeel_type !== b.onderdeel_type) {
      return a.onderdeel_type.localeCompare(b.onderdeel_type);
    }
    return (a.fabrikant || '').localeCompare(b.fabrikant || '');
  });

  // Onderdeel selectie
  const handleOnderdeelSelect = (onderdeel: OnderdeelCatalogus) => {
    setSelectedOnderdeel(onderdeel);
    setOnderdeelSearch(`${onderdeel.onderdeel_type} - ${onderdeel.fabrikant || 'Onbekend'} ${onderdeel.model || ''}`);
    setOnderdeelDropdownOpen(false);
  };

  // Onderdeel toevoegen aan boot
  const handleAddOnderdeel = async () => {
    if (!selectedOnderdeel || !bootId) {
      setError('Selecteer eerst een onderdeel en sla de boot op');
      return;
    }

    try {
      // Bereken vervangingsdatum op basis van levensduur
      let vervangingsdatum = null;
      if (selectedOnderdeel.levensduur_maanden) {
        const installDate = new Date(nieuwOnderdeelDatum);
        installDate.setMonth(installDate.getMonth() + selectedOnderdeel.levensduur_maanden);
        vervangingsdatum = installDate.toISOString().split('T')[0];
      }

      const nieuwOnderdeel = await apiFetch('/api/v1/boot-onderdelen', {
        method: 'POST',
        body: JSON.stringify({
          boot_id: bootId,
          voorraad_item_id: selectedOnderdeel.id,
          onderdeel_type: selectedOnderdeel.onderdeel_type,
          fabrikant: selectedOnderdeel.fabrikant,
          artikelnummer: selectedOnderdeel.artikelnummer,
          positie: nieuwOnderdeelPositie || null,
          serienummer: nieuwOnderdeelSerienummer || null,
          bouwjaar: nieuwOnderdeelBouwjaar === '' ? null : nieuwOnderdeelBouwjaar,
          draaiuren_huidig: nieuwOnderdeelDraaiuren === '' ? null : nieuwOnderdeelDraaiuren,
          installatie_datum: nieuwOnderdeelDatum,
          levensduur_maanden: selectedOnderdeel.levensduur_maanden,
          vervangingsdatum: vervangingsdatum,
        }),
      });

      setBootOnderdelen([nieuwOnderdeel, ...bootOnderdelen]);
      setSelectedOnderdeel(null);
      setOnderdeelSearch('');
      setNieuwOnderdeelDatum(new Date().toISOString().split('T')[0]);
      setNieuwOnderdeelPositie('');
      setNieuwOnderdeelSerienummer('');
      setNieuwOnderdeelBouwjaar('');
      setNieuwOnderdeelDraaiuren('');
    } catch (err: any) {
      setError(err.message || 'Kon onderdeel niet toevoegen');
    }
  };

  // Onderdeel verwijderen
  const handleRemoveOnderdeel = async (onderdeelId: number) => {
    if (!window.confirm('Weet je zeker dat je dit onderdeel wilt verwijderen?')) {
      return;
    }

    try {
      await apiFetch(`/api/v1/boot-onderdelen/${onderdeelId}`, {
        method: 'DELETE',
      });
      setBootOnderdelen(bootOnderdelen.filter(o => o.id !== onderdeelId));
    } catch (err: any) {
      setError(err.message || 'Kon onderdeel niet verwijderen');
    }
  };

  // Onderdeel bewerken - open modal
  const handleEditOnderdeel = (onderdeel: BootOnderdeel) => {
    setEditingOnderdeel(onderdeel);
    setEditDatum(onderdeel.installatie_datum || '');
    setEditLevensduur(onderdeel.levensduur_maanden || '');
    setEditPositie(onderdeel.positie || '');
    setEditSerienummer(onderdeel.serienummer || '');
    setEditBouwjaar(onderdeel.bouwjaar ?? '');
    setEditDraaiuren(onderdeel.draaiuren_huidig ?? '');
  };

  // Onderdeel bewerken - opslaan
  const handleSaveEditOnderdeel = async () => {
    if (!editingOnderdeel) return;

    try {
      // Bereken nieuwe vervangingsdatum
      let vervangingsdatum = null;
      if (editLevensduur && editDatum) {
        const installDate = new Date(editDatum);
        installDate.setMonth(installDate.getMonth() + Number(editLevensduur));
        vervangingsdatum = installDate.toISOString().split('T')[0];
      }

      const updatedOnderdeel = await apiFetch(`/api/v1/boot-onderdelen/${editingOnderdeel.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          installatie_datum: editDatum || null,
          levensduur_maanden: editLevensduur || null,
          vervangingsdatum: vervangingsdatum,
          positie: editPositie || null,
          serienummer: editSerienummer || null,
          bouwjaar: editBouwjaar === '' ? null : editBouwjaar,
          draaiuren_huidig: editDraaiuren === '' ? null : editDraaiuren,
        }),
      });

      // Update de state met de nieuwe data
      setBootOnderdelen(prevOnderdelen =>
        prevOnderdelen.map(o =>
          o.id === editingOnderdeel.id ? {
            ...o,
            installatie_datum: updatedOnderdeel.installatie_datum,
            levensduur_maanden: updatedOnderdeel.levensduur_maanden,
            vervangingsdatum: updatedOnderdeel.vervangingsdatum,
            positie: updatedOnderdeel.positie,
            serienummer: updatedOnderdeel.serienummer,
            bouwjaar: updatedOnderdeel.bouwjaar,
            draaiuren_huidig: updatedOnderdeel.draaiuren_huidig,
          } : o
        )
      );
      setEditingOnderdeel(null);
    } catch (err: any) {
      setError(err.message || 'Kon onderdeel niet bijwerken');
    }
  };

  // Boot model selectie
  const handleBootModelSelect = (model: BootModel) => {
    setFormData(prev => ({
      ...prev,
      boot_model_id: model.id,
      // Vul specificaties automatisch in vanuit boot model
      lengte: model.lengte || prev.lengte,
      breedte: model.breedte || prev.breedte,
      diepgang: model.diepgang || prev.diepgang,
      gewicht: model.gewicht || prev.gewicht,
    }));
    // Voor generieke modellen, toon alleen het model (Motorboot / Zeilboot)
    const displayText = model.fabrikant === 'Onbekend'
      ? `${model.model} (Onbekend merk)`
      : `${model.fabrikant} ${model.model}${model.type_boot ? ` (${model.type_boot})` : ''}`;
    setBootModelSearch(displayText);
    setBootModelDropdownOpen(false);
    setSelectedBootModel(model);
  };

  // Eigenaar selectie
  const handleEigenaarSelect = (relatie: Relation) => {
    setFormData(prev => ({ ...prev, eigenaar_id: relatie.id }));
    setEigenaarSearch(getEigenaarDisplayNaam(relatie));
    setEigenaarDropdownOpen(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    // Als boot_model_id wijzigt, update het geselecteerde model en specificaties
    if (name === 'boot_model_id') {
      const modelId = parseInt(value);
      const model = bootModellen.find(m => m.id === modelId);
      setSelectedBootModel(model || null);

      // Vul specificaties automatisch in vanuit boot model (alleen als ze nog niet ingevuld zijn)
      if (model) {
        setFormData((prev) => ({
          ...prev,
          boot_model_id: modelId,
          // Overschrijf specificaties met model data indien beschikbaar
          lengte: model.lengte || prev.lengte,
          breedte: model.breedte || prev.breedte,
          diepgang: model.diepgang || prev.diepgang,
          gewicht: model.gewicht || prev.gewicht,
        }));
        return; // Return early om dubbele update te voorkomen
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'number'
          ? value === ''
            ? undefined
            : name === 'bouwjaar'
              ? parseInt(value)
              : parseFloat(value) // Voor decimale specificaties
          : value,
    }));
  };

  // Genereer bouwjaar opties op basis van geselecteerd boot model
  const getBouwjaarOptions = (): number[] => {
    if (!selectedBootModel || !selectedBootModel.bouwjaar_vanaf || !selectedBootModel.bouwjaar_tot) {
      return [];
    }

    const start = selectedBootModel.bouwjaar_vanaf;
    const end = selectedBootModel.bouwjaar_tot;
    const years: number[] = [];

    for (let year = start; year <= end; year++) {
      years.push(year);
    }

    return years;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.boot_model_id === 0) {
      setError('Selecteer een boot model');
      return;
    }

    if (formData.eigenaar_id === 0) {
      setError('Selecteer een eigenaar');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEditMode && bootId) {
        await botenService.update(bootId, formData);
      } else {
        await botenService.create(formData);
      }

      setSuccess(isEditMode ? 'Boot succesvol bijgewerkt!' : 'Boot succesvol aangemaakt!');

      setTimeout(() => {
        navigate('/boten');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!bootId || !docBestand) return;
    setDocBezig(true);
    try {
      await bootDocumentenService.upload(bootId, docBestand, {
        document_type: docType,
        titel: docTitel || undefined,
        zichtbaar_portaal: docZichtbaar,
      });
      setDocumenten(await bootDocumentenService.getByBoot(bootId));
      setDocBestand(null);
      setDocTitel('');
      setDocZichtbaar(false);
      setDocTypeEigen(false);
      setDocType('');
    } catch (err: any) {
      setError(err.message || 'Kon het document niet uploaden');
    } finally {
      setDocBezig(false);
    }
  };

  const handleDocumentZichtbaar = async (doc: BootDocument) => {
    if (!bootId) return;
    try {
      await bootDocumentenService.zetZichtbaar(doc.id, !doc.zichtbaar_portaal);
      setDocumenten(await bootDocumentenService.getByBoot(bootId));
    } catch (err: any) {
      setError(err.message || 'Kon het vinkje niet omzetten');
    }
  };

  const handleDocumentVerwijder = async (doc: BootDocument) => {
    if (!bootId) return;
    if (!window.confirm(
      `"${doc.titel || doc.bestand_pad}" verwijderen?\n\n` +
      'Het bestand wordt ook van schijf gehaald. Dit kan niet ongedaan gemaakt worden.'
    )) return;
    try {
      await bootDocumentenService.verwijder(doc.id, true);
      setDocumenten(await bootDocumentenService.getByBoot(bootId));
    } catch (err: any) {
      setError(err.message || 'Kon het document niet verwijderen');
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="boten-form">
      <div className="page-header">
        <h1>
          {isReadOnly ? 'Boot Bekijken' : isEditMode ? 'Boot Bewerken' : 'Nieuwe Boot'}
          {isReadOnly && (
            <span style={{ 
              fontSize: '0.8em', 
              color: '#6c757d', 
              fontWeight: 400,
              marginLeft: '1rem' 
            }}>
              (Alleen-lezen)
            </span>
          )}
        </h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit}>
        {/* Boot Details */}
        <section className="form-section">
          <h2>Boot Informatie</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="boot_model_id">
                Boot Model <span className="required">*</span>
                {formData.boot_model_id > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate(`/boot-modellen/${formData.boot_model_id}`)}
                    style={{
                      marginLeft: '10px',
                      color: '#007bff',
                      textDecoration: 'none',
                      fontSize: '0.85em',
                      padding: '2px 6px',
                      border: '1px solid #007bff',
                      borderRadius: '3px',
                      backgroundColor: '#f0f8ff',
                      cursor: 'pointer'
                    }}
                    title="Ga naar boot model"
                  >
                    🔗 Bekijk
                  </button>
                )}
              </label>
              <div className="searchable-select">
                <input
                  type="text"
                  id="boot_model_id"
                  value={bootModelSearch}
                  onChange={(e) => {
                    setBootModelSearch(e.target.value);
                    setBootModelDropdownOpen(true);
                  }}
                  onFocus={() => setBootModelDropdownOpen(true)}
                  placeholder="Zoek boot model..."
                  required={formData.boot_model_id === 0}
                  autoComplete="off"
                  readOnly={isReadOnly}
                />
                {bootModelDropdownOpen && !isReadOnly && (
                  <div className="dropdown-list">
                    {filteredBootModellen.length > 0 ? (
                      filteredBootModellen.map((model, index) => {
                        const isGeneric = model.fabrikant === 'Onbekend';
                        const showDivider = index > 0 && filteredBootModellen[index - 1].fabrikant === 'Onbekend' && !isGeneric;

                        return (
                          <div key={model.id}>
                            {showDivider && <div className="dropdown-divider"></div>}
                            <div
                              className={`dropdown-item ${formData.boot_model_id === model.id ? 'selected' : ''} ${isGeneric ? 'generic' : ''}`}
                              onClick={() => handleBootModelSelect(model)}
                            >
                              {isGeneric ? (
                                <span>
                                  <strong>{model.model}</strong> <span className="generic-label">(Onbekend merk)</span>
                                </span>
                              ) : (
                                `${model.fabrikant} ${model.model}${model.type_boot ? ` (${model.type_boot})` : ''}`
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="dropdown-item disabled">Geen resultaten gevonden</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="eigenaar_id">
                Eigenaar <span className="required">*</span>
                {formData.eigenaar_id > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate(`/relations/${formData.eigenaar_id}`)}
                    style={{
                      marginLeft: '10px',
                      color: '#007bff',
                      textDecoration: 'none',
                      fontSize: '0.85em',
                      padding: '2px 6px',
                      border: '1px solid #007bff',
                      borderRadius: '3px',
                      backgroundColor: '#f0f8ff',
                      cursor: 'pointer'
                    }}
                    title="Ga naar eigenaar"
                  >
                    🔗 Bekijk
                  </button>
                )}
              </label>
              <div className="searchable-select">
                <input
                  type="text"
                  id="eigenaar_id"
                  value={eigenaarSearch}
                  onChange={(e) => {
                    setEigenaarSearch(e.target.value);
                    setEigenaarDropdownOpen(true);
                  }}
                  onFocus={() => setEigenaarDropdownOpen(true)}
                  placeholder="Zoek eigenaar..."
                  required={formData.eigenaar_id === 0}
                  autoComplete="off"
                />
                {eigenaarDropdownOpen && (
                  <div className="dropdown-list">
                    {filteredRelaties.length > 0 ? (
                      filteredRelaties.map((relatie) => (
                        <div
                          key={relatie.id}
                          className={`dropdown-item ${formData.eigenaar_id === relatie.id ? 'selected' : ''}`}
                          onClick={() => handleEigenaarSelect(relatie)}
                        >
                          {getEigenaarDisplayNaam(relatie)}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled">Geen resultaten gevonden</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="bootnaam">Bootnaam</label>
              <input
                type="text"
                id="bootnaam"
                name="bootnaam"
                value={formData.bootnaam}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="registratienummer">Registratienummer</label>
              <input
                type="text"
                id="registratienummer"
                name="registratienummer"
                value={formData.registratienummer}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bouwjaar">Bouwjaar</label>
              {getBouwjaarOptions().length > 0 ? (
                <select
                  id="bouwjaar"
                  name="bouwjaar"
                  value={formData.bouwjaar ?? ''}
                  onChange={handleChange}
                >
                  <option value="">-- Selecteer bouwjaar --</option>
                  {getBouwjaarOptions().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  id="bouwjaar"
                  name="bouwjaar"
                  value={formData.bouwjaar ?? ''}
                  onChange={handleChange}
                  min="1900"
                  max="2100"
                  placeholder="Selecteer eerst een boot model"
                />
              )}
            </div>

            <div className="form-group">
              <label>Status</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={formData.actief}
                    onChange={(e) => setFormData(prev => ({ ...prev, actief: e.target.checked }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span style={{
                  fontWeight: 600,
                  color: formData.actief ? '#28a745' : '#dc3545'
                }}>
                  {formData.actief ? 'Actief' : 'Inactief'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Specificaties */}
        <section className="form-section">
          <h2>Specificaties</h2>
          {selectedBootModel && (
            <p className="section-description">
              {selectedBootModel.fabrikant === 'Onbekend'
                ? 'Vul hier de specificaties van deze boot in, aangezien het exacte model onbekend is.'
                : 'Deze specificaties zijn overgenomen van het boot model en kunnen worden aangepast voor deze specifieke boot.'}
            </p>
          )}
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="lengte">Lengte (m)</label>
                <input
                  type="number"
                  id="lengte"
                  name="lengte"
                  value={formData.lengte ?? ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="Bijv: 12.50"
                />
              </div>

              <div className="form-group">
                <label htmlFor="breedte">Breedte (m)</label>
                <input
                  type="number"
                  id="breedte"
                  name="breedte"
                  value={formData.breedte ?? ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="Bijv: 4.20"
                />
              </div>

              <div className="form-group">
                <label htmlFor="diepgang">Diepgang (m)</label>
                <input
                  type="number"
                  id="diepgang"
                  name="diepgang"
                  value={formData.diepgang ?? ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="Bijv: 1.30"
                />
              </div>

              <div className="form-group">
                <label htmlFor="gewicht">Gewicht (kg)</label>
                <input
                  type="number"
                  id="gewicht"
                  name="gewicht"
                  value={formData.gewicht ?? ''}
                  onChange={handleChange}
                  step="1"
                  min="0"
                  placeholder="Geheel getal in kg (bijv: 8500)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="materiaal">Materiaal</label>
                <input
                  type="text"
                  id="materiaal"
                  name="materiaal"
                  value={formData.materiaal}
                  onChange={handleChange}
                  placeholder="Bijv: Staal, Polyester, Aluminium"
                />
              </div>
            </div>
        </section>

        {/* Locatie */}
        <section className="form-section">
          <h2>Locatie</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="ligplaats">Ligplaats</label>
              <input
                type="text"
                id="ligplaats"
                name="ligplaats"
                value={formData.ligplaats}
                onChange={handleChange}
                placeholder="Bijv: Steiger 3, Haven Amsterdam"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mmsi">MMSI Nummer</label>
              <input
                type="text"
                id="mmsi"
                name="mmsi"
                value={formData.mmsi}
                onChange={handleChange}
                maxLength={9}
                pattern="\d{9}"
                placeholder="9 cijfers"
              />
            </div>
          </div>
        </section>

        {/* Onderdelen */}
        {isEditMode && (
          <section className="form-section">
            <h2>Geïnstalleerde Onderdelen</h2>
            <p className="section-description">
              Voeg onderdelen toe die in deze boot zijn geïnstalleerd. De vervangingsdatum wordt automatisch berekend op basis van de levensduur.
            </p>

            {/* Onderdeel toevoegen formulier */}
            {!isReadOnly && (
              <div className="onderdeel-add-form" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '6px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 150px 150px', gap: '1rem', alignItems: 'end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Onderdeel uit catalogus</label>
                  <div className="searchable-select">
                    <input
                      type="text"
                      value={onderdeelSearch}
                      onChange={(e) => {
                        setOnderdeelSearch(e.target.value);
                        setOnderdeelDropdownOpen(true);
                      }}
                      onFocus={() => setOnderdeelDropdownOpen(true)}
                      placeholder="Zoek onderdeel (type, fabrikant, model)..."
                      autoComplete="off"
                    />
                    {onderdeelDropdownOpen && (
                      <div className="dropdown-list" style={{ maxHeight: '300px' }}>
                        {filteredOnderdelen.length > 0 ? (
                          filteredOnderdelen.slice(0, 50).map((onderdeel) => (
                            <div
                              key={onderdeel.id}
                              className={`dropdown-item ${selectedOnderdeel?.id === onderdeel.id ? 'selected' : ''}`}
                              onClick={() => handleOnderdeelSelect(onderdeel)}
                            >
                              <strong>{onderdeel.onderdeel_type}</strong>
                              <br />
                              <span style={{ fontSize: '0.85em', color: '#666' }}>
                                {onderdeel.fabrikant || 'Onbekend'} {onderdeel.model || ''} - {onderdeel.leverancier || 'n.v.t.'}
                                {onderdeel.levensduur_maanden && ` (${onderdeel.levensduur_maanden} mnd)`}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="dropdown-item disabled">Geen onderdelen gevonden</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Positie</label>
                  <input
                    type="text"
                    value={nieuwOnderdeelPositie}
                    onChange={(e) => setNieuwOnderdeelPositie(e.target.value)}
                    placeholder="Bakboord, voorpiek..."
                    style={{ width: '9rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Serienummer</label>
                  <input
                    type="text"
                    value={nieuwOnderdeelSerienummer}
                    onChange={(e) => setNieuwOnderdeelSerienummer(e.target.value)}
                    placeholder="s/n"
                    style={{ width: '9rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Bouwjaar</label>
                  <input
                    type="number"
                    min="1900"
                    max="2100"
                    value={nieuwOnderdeelBouwjaar}
                    onChange={(e) => setNieuwOnderdeelBouwjaar(e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="2021"
                    style={{ width: '6rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Draaiuren</label>
                  <input
                    type="number"
                    min="0"
                    value={nieuwOnderdeelDraaiuren}
                    onChange={(e) => setNieuwOnderdeelDraaiuren(e.target.value ? parseInt(e.target.value) : '')}
                    style={{ width: '6rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Installatie datum</label>
                  <input
                    type="date"
                    value={nieuwOnderdeelDatum}
                    onChange={(e) => setNieuwOnderdeelDatum(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAddOnderdeel}
                  disabled={!selectedOnderdeel}
                  style={{ height: '42px' }}
                >
                  + Toevoegen
                </button>
              </div>
            </div>
            )}

            {/* Onderdelen tabel */}
            <div className="onderdelen-table" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f1f3f5' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Onderdeel</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Positie / serienr.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Installatie</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Levensduur</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Vervangdatum</th>
                    {!isReadOnly && (
                      <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Actie</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bootOnderdelen.length === 0 ? (
                    <tr>
                      <td colSpan={isReadOnly ? 5 : 6} style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                        Nog geen onderdelen toegevoegd
                      </td>
                    </tr>
                  ) : (
                    bootOnderdelen.map((onderdeel) => {
                      const isExpired = onderdeel.vervangingsdatum && new Date(onderdeel.vervangingsdatum) < new Date();
                      // Haal actuele catalogus data op als beschikbaar
                      const catalogusData = onderdeel.voorraad_item_id
                        ? onderdelenCatalogus.find(c => c.id === onderdeel.voorraad_item_id)
                        : null;
                      const displayType = catalogusData?.onderdeel_type || onderdeel.onderdeel_type;
                      const displayFabrikant = catalogusData?.fabrikant || onderdeel.fabrikant;
                      const displayModel = catalogusData?.model || '';
                      const displayArtikel = catalogusData?.artikelnummer || onderdeel.artikelnummer;

                      return (
                        <tr key={onderdeel.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '0.75rem' }}>
                            <strong>{displayType}</strong>
                            <br />
                            <span style={{ fontSize: '0.85em', color: '#666' }}>
                              {displayFabrikant || 'Onbekend'} {displayModel} - {displayArtikel || 'n.v.t.'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {onderdeel.positie || '\u2014'}
                            {onderdeel.serienummer && (
                              <>
                                <br />
                                <span style={{ fontSize: '0.85em', color: '#666' }}>
                                  s/n {onderdeel.serienummer}
                                  {onderdeel.bouwjaar ? ` (${onderdeel.bouwjaar})` : ''}
                                </span>
                              </>
                            )}
                            {onderdeel.draaiuren_huidig != null && (
                              <>
                                <br />
                                <span style={{ fontSize: '0.85em', color: '#666' }}>
                                  {onderdeel.draaiuren_huidig} draaiuren
                                </span>
                              </>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {onderdeel.installatie_datum
                              ? new Date(onderdeel.installatie_datum).toLocaleDateString('nl-NL')
                              : '\u2014'}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {onderdeel.levensduur_maanden ? `${onderdeel.levensduur_maanden} maanden` : 'Onbepaald'}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {onderdeel.vervangingsdatum ? (
                              <span style={{
                                color: isExpired ? '#dc3545' : '#28a745',
                                fontWeight: isExpired ? 'bold' : 'normal'
                              }}>
                                {new Date(onderdeel.vervangingsdatum).toLocaleDateString('nl-NL')}
                                {isExpired && ' (verlopen)'}
                              </span>
                            ) : (
                              'n.v.t.'
                            )}
                          </td>
                          {!isReadOnly && (
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn-small btn-secondary"
                                  onClick={() => handleEditOnderdeel(onderdeel)}
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                >
                                  Bewerken
                                </button>
                                {onderdeel.voorraad_item_id && (
                                  <button
                                    type="button"
                                    className="btn-small btn-info"
                                    onClick={() => navigate(`/onderdelen/${onderdeel.voorraad_item_id}/documenten`)}
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                  >
                                    Documenten
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn-small btn-danger"
                                  onClick={() => handleRemoveOnderdeel(onderdeel.id)}
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                >
                                  Verwijderen
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Scheepsdocumenten - CE-verklaring, schema's, handleidingen.
            Hangen aan deze boot, niet aan het model: het dossier hoort bij het
            bouwnummer. Bestanden gaan naar Documenten/<letter>/<klant>/<bootnaam>/. */}
        {isEditMode && (
          <section className="form-section">
            <h2>Documenten</h2>
            <p className="section-description">
              CE-verklaring, technisch dossier, schema's en handleidingen van deze boot.
            </p>

            {!isReadOnly && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '6px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 1fr 130px', gap: '1rem', alignItems: 'end' }}>
                  {/* Een <datalist> liet niets zien zodra er al een volledige waarde
                      in stond, dus een echte keuzelijst met "Anders" voor eigen invoer. */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Soort</label>
                    <select
                      value={docTypeEigen ? '__anders__' : docType}
                      onChange={(e) => {
                        if (e.target.value === '__anders__') {
                          setDocTypeEigen(true);
                          setDocType('');
                        } else {
                          setDocTypeEigen(false);
                          setDocType(e.target.value);
                        }
                      }}
                    >
                      <option value="">Kies een soort…</option>
                      {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      <option value="__anders__">Anders…</option>
                    </select>
                    {docTypeEigen && (
                      <input
                        type="text"
                        value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                        placeholder="Eigen soort"
                        style={{ marginTop: '0.5rem' }}
                        autoFocus
                      />
                    )}
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Titel</label>
                    <input
                      type="text"
                      value={docTitel}
                      onChange={(e) => setDocTitel(e.target.value)}
                      placeholder="Leeg laten = bestandsnaam"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Bestand</label>
                    <input
                      type="file"
                      onChange={(e) => setDocBestand(e.target.files?.[0] || null)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleDocumentUpload}
                    disabled={!docBestand || !docType || docBezig}
                  >
                    {docBezig ? 'Bezig...' : '+ Toevoegen'}
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={docZichtbaar}
                    onChange={(e) => setDocZichtbaar(e.target.checked)}
                  />
                  Zichtbaar in klantportaal
                </label>
              </div>
            )}

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Soort</th>
                    <th>Titel</th>
                    <th>Toegevoegd</th>
                    <th style={{ width: '110px' }}>Portaal</th>
                    <th style={{ width: '180px' }}>Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {documenten.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem', color: '#666' }}>
                        Nog geen documenten toegevoegd
                      </td>
                    </tr>
                  ) : (
                    documenten.map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.document_type}</td>
                        <td>{doc.titel || doc.bestand_pad.split('/').pop()}</td>
                        <td>{doc.created_at ? new Date(doc.created_at).toLocaleDateString('nl-NL') : '-'}</td>
                        <td>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <input
                              type="checkbox"
                              checked={doc.zichtbaar_portaal}
                              disabled={isReadOnly}
                              onChange={() => handleDocumentZichtbaar(doc)}
                            />
                            {doc.zichtbaar_portaal ? 'Zichtbaar' : 'Intern'}
                          </label>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => bootDocumentenService.open(doc.id)}
                          >
                            Openen
                          </button>
                          {!isReadOnly && (
                            <button
                              type="button"
                              className="btn-danger btn-sm"
                              style={{ marginLeft: '0.5rem' }}
                              onClick={() => handleDocumentVerwijder(doc)}
                            >
                              Verwijderen
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/boten')}
          >
            {isReadOnly ? 'Terug' : 'Annuleren'}
          </button>
          {canEdit && (
            <button type="submit" className="btn-success" disabled={loading}>
              {loading ? 'Bezig...' : isEditMode ? 'Bijwerken' : 'Aanmaken'}
            </button>
          )}
        </div>
      </form>

      {/* Bewerken Modal */}
      {editingOnderdeel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>
              Onderdeel Bewerken
            </h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              {(() => {
                const catalogusData = editingOnderdeel.voorraad_item_id
                  ? onderdelenCatalogus.find(c => c.id === editingOnderdeel.voorraad_item_id)
                  : null;
                const displayType = catalogusData?.onderdeel_type || editingOnderdeel.onderdeel_type;
                const displayFabrikant = catalogusData?.fabrikant || editingOnderdeel.fabrikant;
                const displayModel = catalogusData?.model || '';
                const displayArtikel = catalogusData?.artikelnummer || editingOnderdeel.artikelnummer;
                return (
                  <>
                    <strong>{displayType}</strong><br />
                    {displayFabrikant || 'Onbekend'} {displayModel} - {displayArtikel || 'n.v.t.'}
                  </>
                );
              })()}
            </p>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Positie</label>
              <input
                type="text"
                value={editPositie}
                onChange={(e) => setEditPositie(e.target.value)}
                placeholder="Bakboord, stuurboord, voorpiek..."
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Serienummer</label>
              <input
                type="text"
                value={editSerienummer}
                onChange={(e) => setEditSerienummer(e.target.value)}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Bouwjaar</label>
              <input
                type="number"
                min="1900"
                max="2100"
                value={editBouwjaar}
                onChange={(e) => setEditBouwjaar(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="Van het typeplaatje"
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Draaiuren</label>
              <input
                type="number"
                min="0"
                value={editDraaiuren}
                onChange={(e) => setEditDraaiuren(e.target.value ? parseInt(e.target.value) : '')}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Installatie datum</label>
              <input
                type="date"
                value={editDatum}
                onChange={(e) => setEditDatum(e.target.value)}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Levensduur (maanden)</label>
              <input
                type="number"
                value={editLevensduur}
                onChange={(e) => setEditLevensduur(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="Bijv: 120"
                min="0"
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setEditingOnderdeel(null)}
              >
                Annuleren
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveEditOnderdeel}
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default BotenForm;